/**
 * Logger Utility for VulnZero Frontend
 *
 * Provides environment-aware logging with Sentry integration
 * - Development: Logs to console
 * - Production: Optionally sends to Sentry as breadcrumbs
 */

import { addBreadcrumb } from './sentry'

const isDev = import.meta.env.DEV
const isVerbose = import.meta.env.VITE_VERBOSE_LOGGING === 'true'

// Log paths only: query parameters, fragments and URL credentials can carry
// auth/SSO tokens. Unknown schemes and malformed URLs must fail closed.
function logPath(url) {
  if (typeof url !== 'string') return '[invalid URL]'
  try {
    const parsed = new URL(url, 'https://logging.invalid')
    return ['http:', 'https:'].includes(parsed.protocol)
      ? parsed.pathname
      : '[invalid URL]'
  } catch {
    return '[invalid URL]'
  }
}

function logMethod(method) {
  const normalized = typeof method === 'string' ? method.toUpperCase() : ''
  return ['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'HEAD', 'OPTIONS'].includes(normalized)
    ? normalized : 'UNKNOWN'
}

/**
 * Logger class with multiple log levels
 */
class Logger {
  /**
   * Log debug message (development only)
   * @param {string} message - Log message
   * @param {Object} data - Additional data
   */
  debug(message, data = {}) {
    if (isDev || isVerbose) {
      console.log(`🔍 [DEBUG] ${message}`, data)
    }

    // Add breadcrumb for debugging in Sentry
    if (!isDev) {
      addBreadcrumb(message, data, 'debug')
    }
  }

  /**
   * Log info message
   * @param {string} message - Log message
   * @param {Object} data - Additional data
   */
  info(message, data = {}) {
    if (isDev || isVerbose) {
      console.log(`ℹ️  [INFO] ${message}`, data)
    }

    // Add breadcrumb in production
    if (!isDev) {
      addBreadcrumb(message, data, 'info')
    }
  }

  /**
   * Log warning message
   * @param {string} message - Log message
   * @param {Object} data - Additional data
   */
  warn(message, data = {}) {
    if (isDev || isVerbose) {
      console.warn(`⚠️  [WARN] ${message}`, data)
    }

    // Always add warning breadcrumb
    addBreadcrumb(message, data, 'warning')
  }

  /**
   * Log error message
   * @param {string} message - Log message
   * Additional error arguments are deliberately ignored (may contain secrets).
   * The message must be a static, credential-free label.
   */
  error(message) {
    // Axios errors contain request bodies, Authorization headers and response
    // tokens. Even error.message can embed credentials. Keep only the caller's
    // static label; never forward the error object to console or telemetry.
    if (isDev || isVerbose) {
      console.error(`❌ [ERROR] ${message}`)
    }

    addBreadcrumb(message, {}, 'error')
  }

  /**
   * Log WebSocket event (development and verbose mode only)
   * @param {string} event - Event name
   * @param {Object} data - Event data
   */
  websocket(event, data = {}) {
    if (isDev || isVerbose) {
      const emoji = this._getWebSocketEmoji(event)
      console.log(`${emoji} [WebSocket] ${event}`, data)
    }

    // Add breadcrumb for important WebSocket events
    const importantEvents = ['connect', 'disconnect', 'error', 'reconnect']
    if (importantEvents.includes(event.toLowerCase())) {
      addBreadcrumb(`WebSocket: ${event}`, data, 'websocket')
    }
  }

  /**
   * Log API request (development and verbose mode only)
   * @param {string} method - HTTP method
   * @param {string} url - Request URL
   * Payload arguments are deliberately ignored; logs contain metadata only.
   */
  apiRequest(method, url) {
    if (isDev || isVerbose) {
      console.log(`🚀 [API Request] ${logMethod(method)} ${logPath(url)}`)
    }
  }

  /**
   * Log API response (development and verbose mode only)
   * @param {string} method - HTTP method
   * @param {string} url - Request URL
   * Payload arguments are deliberately ignored; logs contain metadata only.
   */
  apiResponse(method, url) {
    if (isDev || isVerbose) {
      console.log(`✅ [API Response] ${logMethod(method)} ${logPath(url)}`)
    }
  }

  /**
   * Log API error (always logged)
   * @param {string} method - HTTP method
   * @param {string} url - Request URL
   * @param {number} status - HTTP status code
   * Payload arguments are deliberately ignored; logs contain metadata only.
   */
  apiError(method, url, status) {
    const safeMethod = logMethod(method)
    const safePath = logPath(url)
    const safeStatus = Number.isInteger(status) && status >= 0 && status <= 599 ? status : 0
    if (isDev || isVerbose) {
      console.error(`❌ [API Error] ${safeMethod} ${safePath} - ${safeStatus}`)
    }

    // Validation errors can echo passwords and submitted tokens. Never send
    // backend error bodies to telemetry, even in production.
    addBreadcrumb(`API Error: ${safeMethod} ${safePath}`, { status: safeStatus }, 'http')
  }

  /**
   * Get emoji for WebSocket event type
   * @param {string} event - Event name
   * @returns {string} Emoji
   * @private
   */
  _getWebSocketEmoji(event) {
    const eventLower = event.toLowerCase()

    if (eventLower.includes('connect')) return '✅'
    if (eventLower.includes('disconnect')) return '❌'
    if (eventLower.includes('error')) return '⚠️'
    if (eventLower.includes('deployment')) return '🚀'
    if (eventLower.includes('vulnerability')) return '🔍'
    if (eventLower.includes('patch')) return '🔧'
    if (eventLower.includes('alert')) return '🚨'
    if (eventLower.includes('health')) return '💚'
    if (eventLower.includes('metrics')) return '📊'
    if (eventLower.includes('progress')) return '📊'
    if (eventLower.includes('rollback')) return '⏪'

    return '📡'
  }

  /**
   * Create a performance timer
   * @param {string} label - Timer label
   * @returns {Object} Timer object with stop() method
   */
  time(label) {
    const startTime = performance.now()

    return {
      stop: () => {
        const duration = performance.now() - startTime
        if (isDev || isVerbose) {
          console.log(`⏱️  [TIMING] ${label}: ${duration.toFixed(2)}ms`)
        }

        // Add performance breadcrumb
        addBreadcrumb(`Timing: ${label}`, { duration: `${duration.toFixed(2)}ms` }, 'performance')
      },
    }
  }

  /**
   * Group console logs (development only)
   * @param {string} label - Group label
   * @param {Function} callback - Callback function
   */
  group(label, callback) {
    if (isDev) {
      console.group(label)
      callback()
      console.groupEnd()
    } else {
      callback()
    }
  }

  /**
   * Log table data (development only)
   * @param {Array|Object} data - Data to display
   */
  table(data) {
    if (isDev) {
      console.table(data)
    }
  }
}

// Export singleton instance
export const logger = new Logger()
export default logger
